/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Status Documents Component
 *
 * Migrated from: lego/page/eos/overview/StatusDocuments/StatusDocuments.jsx (1,007 lines)
 *
 * Filter panel (Formik + Yup) with 3 table view modes:
 *   - Workflow table (non-legacy default)
 *   - User Access table
 *   - Legacy download/acknowledgement table (legacy only)
 *
 * Reuses existing rcm-new components:
 *   - Select (~/components/Select/Select)
 *   - FilterTrigger (~/components/shared/FilterTrigger)
 *   - ErrorMessageStyled (~/components/ErrorMessage)
 *   - ActionsMenuBig (~/components/shared/ActionsMenuBig)
 */

import { useEffect, useState, useRef } from 'react'
import { Button, FormGroup, Collapse, Form } from 'react-bootstrap'
import { Formik, Field, type FormikProps } from 'formik'
import { yupToFormErrors } from 'formik'
import cloneDeep from 'lodash/cloneDeep'
import sortBy from 'lodash/sortBy'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../../root'
import { useModal } from '../../../../contexts/ModalContext'
import { eosApiService } from '../../../../services/api/api.eos'
import { EOS_ACTIVITY, EOS_ACKNOWLEDGEMENT_STATUS, EOS_DOWNLOAD_STATUS } from '../../../../constants/eos'
import { Select } from '../../../../components/Select/Select'
import { ErrorMessageStyled } from '../../../../components/ErrorMessage'
import ActionsMenuBig from '../../../../components/shared/ActionsMenuBig'
import { FilterTrigger } from '../../../../components/shared/FilterTrigger'
import EosFormValidationSchema from './EosFormValidation'
import ExportInProcessModal from './ExportInProcessModal'
import EosOverviewAccessTable from './EosOverviewAccessTable'
import EosOverviewLegacyTable from './EosOverviewLegacyTable'
import EosOverviewWorkflowsTable from './EosOverviewWorkflowsTable'
import Loader from '../../../../components/shared/Loader'
import type {
  EosStatusDocumentsProps,
  EosSelectOption,
  EosDocumentsFormData,
  EosDocumentsFilterParams,
  EosDocumentsFilterData,
  EosAssignedStudy,
  EosSiteInfo,
  EosWorkflowTableRow,
  EosAccessTableRow,
  EosLegacyTableRow
} from '../types'

// Simple control label component
const ControlLabel: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <label className={`control-label ${className || ''}`}>{children}</label>
)

// Constants
const LEVEL = {
  STUDY: 'STUDY',
  COUNTRY: 'COUNTRY',
  SITE: 'SITE'
} as const

const orderedPropsCaps = ['STUDY', 'COUNTRY', 'SITE']

interface FilterFormState {
  level?: string
  STUDY?: EosSelectOption | null
  COUNTRY?: EosSelectOption | null
  SITE?: EosSelectOption | null
  activity?: string
  viewMode?: string
  status?: string
}

interface FormValues {
  filter: {
    data: EosDocumentsFormData
  }
}

type FileRow = EosWorkflowTableRow | EosAccessTableRow | EosLegacyTableRow

const WF_STATUSES = {
  ALL: 'ALL',
  NOT_STARTED: 'NOT_STARTED',
  DOWNLOAD: 'DOWNLOAD',
  ACKNOWLEDGEMENT: 'ACKNOWLEDGEMENT',
  COMPLETED: 'COMPLETED'
} as const

const EosStatusDocuments: React.FC<EosStatusDocumentsProps> = ({
  filter: externalFilter,
  isLegacy,
  activitiesOptions
}) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { showModal } = useModal()
  const formRefLocal = useRef<FormikProps<FormValues>>(null)
  const validationSchema = EosFormValidationSchema()

  // View mode options for non-legacy
  const VIEW_MODES: EosSelectOption[] = [
    { value: EOS_ACTIVITY.WORKFLOW, label: t('EOS_DASHBOARD_VIEW_MODE_WORKFLOW') },
    { value: EOS_ACTIVITY.USER_ACCESS, label: t('EOS_DASHBOARD_VIEW_MODE_USER_ACCESS') }
  ]

  // View mode options for legacy
  const VIEW_MODES_LEGACY: EosSelectOption[] = [
    { value: EOS_ACTIVITY.DOWNLOAD, label: t('EOS_ACTIVITY_DOWNLOAD') },
    { value: EOS_ACTIVITY.ACKNOWLEDGEMENT, label: t('EOS_ACTIVITY_ACKNOWLEDGEMENT') },
    { value: EOS_ACTIVITY.USER_ACCESS, label: t('EOS_ACTIVITY_USER_ACCESS') }
  ]

  const WF_STATUS_OPTIONS: EosSelectOption[] = [
    { value: WF_STATUSES.ALL, label: t('ALL') },
    { value: WF_STATUSES.NOT_STARTED, label: t('EOS_DASHBOARD_WORKFLOW_STATUS_OPTION_NOT_STARTED') },
    { value: WF_STATUSES.DOWNLOAD, label: t('EOS_DASHBOARD_WORKFLOW_STATUS_OPTION_DOWNLOAD') },
    {
      value: WF_STATUSES.ACKNOWLEDGEMENT,
      label: t('EOS_DASHBOARD_WORKFLOW_STATUS_OPTION_ACKNOWLEDGEMENT')
    },
    { value: WF_STATUSES.COMPLETED, label: t('EOS_DASHBOARD_WORKFLOW_STATUS_OPTION_COMPLETED') }
  ]

  const ACKNOWLEDGEMENT_STATUSES: EosSelectOption[] = [
    { value: EOS_ACKNOWLEDGEMENT_STATUS.ALL, label: t('ETMF_COMPLETION_TYPE_ALL') },
    { value: EOS_ACKNOWLEDGEMENT_STATUS.NOT_STARTED, label: t('EOS_COMPLETION_TYPE_NOT_STARTED') },
    {
      value: EOS_ACKNOWLEDGEMENT_STATUS.IN_PROGRESS,
      label: t('ETMF_COMPLETION_TYPE_IN_PROGRESS_DOCUMENTS')
    },
    {
      value: EOS_ACKNOWLEDGEMENT_STATUS.COMPLETED,
      label: t('ETMF_COMPLETION_TYPE_COMPLETED_DOCUMENTS')
    }
  ]

  const DOWNLOAD_STATUSES: EosSelectOption[] = [
    { value: EOS_DOWNLOAD_STATUS.ALL, label: t('ALL') },
    { value: EOS_DOWNLOAD_STATUS.OCCURRED, label: t('EOS_DOWNLOAD_STATUS_OCCURRED') },
    { value: EOS_DOWNLOAD_STATUS.NOT_OCCURRED, label: t('EOS_DOWNLOAD_STATUS_NOT_OCCURRED') }
  ]

  const defaultFormData: EosDocumentsFormData = {
    study: 0,
    countries: 0,
    sites: 0,
    level: 'STUDY',
    activity: isLegacy ? EOS_ACTIVITY.DOWNLOAD : WF_STATUSES.ALL,
    ackn_status: isLegacy ? EOS_ACKNOWLEDGEMENT_STATUS.ALL : '',
    download_status: isLegacy ? EOS_DOWNLOAD_STATUS.ALL : '',
    viewMode: EOS_ACTIVITY.WORKFLOW
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [studyDocumentsFilterOptions, setStudyDocumentsFilterOptions] = useState<Record<string, EosSelectOption[]>>({})
  const [filterForm, setFilterForm] = useState<FilterFormState>({})
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true)
  const [wasFiltered, setWasFiltered] = useState(false)
  const [tableViewMode, setTableViewMode] = useState<string>()
  const [filesList, setFilesList] = useState<FileRow[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [initialValues, setInitialValues] = useState<{ data: EosDocumentsFormData }>({
    data: cloneDeep(defaultFormData)
  })

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  useEffect(() => {
    setFilterDefaults()
    downloadStudies()

    if (externalFilter && Object.keys(externalFilter).length > 0) {
      injectFilter(externalFilter)
      setIsFilterCollapsed(false)
    }
  }, [])

  useEffect(() => {
    if (externalFilter && Object.keys(externalFilter).length > 0) {
      injectFilter(externalFilter)
      setIsFilterCollapsed(true)
    }
  }, [externalFilter])

  // ---------------------------------------------------------------------------
  // Visibility helpers
  // ---------------------------------------------------------------------------

  const showStudy = (filterFormData: FilterFormState = filterForm): boolean => {
    return ([LEVEL.STUDY, LEVEL.COUNTRY, LEVEL.SITE] as string[]).includes(filterFormData.level as string)
  }

  const showCountry = (filterFormData: FilterFormState = filterForm): boolean => {
    return ([LEVEL.COUNTRY, LEVEL.SITE] as string[]).includes(filterFormData.level as string)
  }

  const showSite = (filterFormData: FilterFormState = filterForm): boolean => {
    return filterFormData.level === LEVEL.SITE
  }

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const downloadStudies = async (): Promise<EosSelectOption[]> => {
    const data = (await eosApiService.getAssignedStudies()) as unknown as EosAssignedStudy[]
    const studyOptions = convertServerValues(
      data.filter((study) => !(isLegacy ? !study.legacy : study.legacy) && !study.archived),
      'study'
    )
    setStudyDocumentsFilterOptions((prev) => ({ ...prev, STUDY: studyOptions }))
    return studyOptions
  }

  const convertServerValues = (
    input: EosAssignedStudy[] | string[] | EosSiteInfo[],
    type: string
  ): EosSelectOption[] => {
    const output: EosSelectOption[] = []

    input.forEach((item: unknown) => {
      switch (type) {
        case 'study': {
          const study = item as EosAssignedStudy
          output.push({ value: study.studyFolderId, label: study.studyName })
          break
        }
        case 'country': {
          const country = item as string
          output.push({ value: country, label: country })
          break
        }
        case 'site': {
          const site = item as EosSiteInfo
          output.push({ value: site.siteFolderId, label: site.siteName })
          break
        }
      }
    })

    return sortBy(output, (item) => item.label?.toLowerCase())
  }

  // ---------------------------------------------------------------------------
  // Filter logic
  // ---------------------------------------------------------------------------

  const setFilterDefaults = () => {
    const filterFormData: FilterFormState = {
      level: LEVEL.STUDY,
      viewMode: EOS_ACTIVITY.WORKFLOW,
      activity: isLegacy ? EOS_ACTIVITY.DOWNLOAD : WF_STATUSES.ALL,
      status: WF_STATUSES.ALL
    }
    setFilterForm(filterFormData)
    setViewMode()
  }

  const setViewMode = () => {
    const formDataLocal = formRefLocal.current?.values?.filter?.data
    if (!formDataLocal) return

    const tableViewModeData = isLegacy
      ? formDataLocal.activity
      : formDataLocal.viewMode === EOS_ACTIVITY.USER_ACCESS
        ? EOS_ACTIVITY.USER_ACCESS
        : EOS_ACTIVITY.WORKFLOW

    setTableViewMode(tableViewModeData)
  }

  const injectFilter = (filterProp: EosDocumentsFilterData) => {
    const levelFilter = filterProp?.model
    if (!levelFilter) return

    const filterFormData = cloneDeep(filterForm)
    const currentFormData = formRefLocal.current?.values?.filter?.data
    if (!currentFormData) return

    // Clone formData so we don't mutate Formik's internal state in-place.
    // Mutating in-place corrupts Formik's previous initialValues reference,
    // causing enableReinitialize's deep comparison to see old === new,
    // which prevents the form from reinitializing on the first click.
    const formData = cloneDeep(currentFormData)

    if (levelFilter.type) {
      filterFormData.level = levelFilter.type
      formData.level = levelFilter.type as EosDocumentsFormData['level']
    }
    if (levelFilter.study) {
      filterFormData.STUDY = levelFilter.study
      formData.study = levelFilter.study.value as number
    }
    if (levelFilter.site) {
      filterFormData.SITE = levelFilter.site
      formData.sites = levelFilter.site.value
    }
    if (levelFilter.country && ([LEVEL.COUNTRY, LEVEL.SITE] as string[]).includes(levelFilter.type as string)) {
      filterFormData.COUNTRY = levelFilter.country
      formData.countries = levelFilter.country.value
    }

    if (isLegacy) {
      if (levelFilter.activity) {
        filterFormData.activity = levelFilter.activity
        formData.activity = levelFilter.activity
      }
      if (filterProp.status) {
        filterFormData.status = filterProp.status
        switch (levelFilter.activity) {
          case 'DOWNLOAD':
            formData.download_status = filterProp.status
            break
          case 'ACKNOWLEDGEMENT':
            formData.ackn_status = filterProp.status
            break
        }
      }
    } else {
      filterFormData.viewMode = EOS_ACTIVITY.WORKFLOW
      filterFormData.activity = filterProp.status ?? WF_STATUSES.ALL
      formData.viewMode = EOS_ACTIVITY.WORKFLOW
      formData.activity = filterProp.status ?? WF_STATUSES.ALL
    }

    // Point Formik's ref to the new clone so getAssembledData reads correct values immediately
    if (formRefLocal.current?.values?.filter) {
      formRefLocal.current.values.filter.data = formData
    }

    setInitialValues({ data: formData })
    setFilterForm(filterFormData)

    onLevelChange(true, false, filterFormData)
    doFilter()
  }

  const getAssembledData = (forExport?: boolean): EosDocumentsFilterParams => {
    const formData = formRefLocal.current?.values?.filter?.data
    if (!formData) return { activityTypes: [], level: '', filterOnlyStudyFolder: false }

    const request: EosDocumentsFilterParams = {
      level: formData.level,
      studyFolderId: formData.study,
      export: !!forExport,
      filterOnlyStudyFolder: false,
      activityTypes: isLegacy
        ? [formData.activity]
        : (() => {
            if (formData.viewMode === EOS_ACTIVITY.USER_ACCESS) {
              return [EOS_ACTIVITY.USER_ACCESS]
            }
            if (formData.activity === EOS_ACTIVITY.DOWNLOAD || formData.activity === WF_STATUSES.NOT_STARTED) {
              return [EOS_ACTIVITY.DOWNLOAD]
            }
            if (formData.activity === EOS_ACTIVITY.ACKNOWLEDGEMENT || formData.activity === WF_STATUSES.COMPLETED) {
              return [EOS_ACTIVITY.ACKNOWLEDGEMENT]
            }
            return [EOS_ACTIVITY.DOWNLOAD, EOS_ACTIVITY.ACKNOWLEDGEMENT]
          })()
    }

    if (formData.countries !== undefined && formData.countries !== 0) {
      request.countries = formData.countries as string | null
    }
    if (formData.sites !== undefined && formData.sites !== 0) {
      request.siteFolderIds = formData.sites as number | null
    }

    if (isLegacy) {
      if (request.activityTypes.includes(EOS_ACTIVITY.ACKNOWLEDGEMENT)) {
        request.acknowledgementStatus = formData.ackn_status
      } else if (request.activityTypes.includes(EOS_ACTIVITY.DOWNLOAD)) {
        request.downloadStatus = formData.download_status
      }
    } else {
      if (formData.activity === WF_STATUSES.NOT_STARTED) {
        request.downloadPhaseStatus = EOS_ACKNOWLEDGEMENT_STATUS.NOT_STARTED
      } else if (formData.activity === EOS_ACTIVITY.ACKNOWLEDGEMENT) {
        request.acknowledgementStatus = EOS_ACKNOWLEDGEMENT_STATUS.IN_PROGRESS
      } else if (formData.activity === EOS_ACTIVITY.DOWNLOAD) {
        request.downloadPhaseStatus = EOS_ACKNOWLEDGEMENT_STATUS.IN_PROGRESS
      } else if (formData.activity === WF_STATUSES.COMPLETED) {
        request.acknowledgementStatus = EOS_ACKNOWLEDGEMENT_STATUS.COMPLETED
      } else {
        request.downloadPhaseStatus = EOS_ACKNOWLEDGEMENT_STATUS.ALL
        request.acknowledgementStatus = EOS_ACKNOWLEDGEMENT_STATUS.ALL
      }
    }

    return request
  }

  // ---------------------------------------------------------------------------
  // API calls
  // ---------------------------------------------------------------------------

  const getItems = async (
    queryParams: Record<string, unknown> = {},
    skipFilterCheck = false
  ): Promise<{
    elements: FileRow[]
    totalElements: number
  } | null> => {
    if (!wasFiltered && !skipFilterCheck) return null

    const requestData = getAssembledData()
    const filterParams = { ...requestData, ...queryParams }

    const apiCallSites = eosApiService.getEosStatusDocuments({
      ...filterParams,
      filterOnlyStudyFolder: false,
      activityTypes: filterParams.activityTypes?.join(',')
    } as any) as Promise<{
      elements: FileRow[]
    }>
    const apiCallStudies =
      queryParams.pageStart === 0 && requestData.level === LEVEL.STUDY
        ? (eosApiService.getEosStatusDocuments({
            ...filterParams,
            filterOnlyStudyFolder: true,
            activityTypes: filterParams.activityTypes?.join(',')
          } as any) as Promise<{
            elements: FileRow[]
          }>)
        : null

    const [dataSites, dataStudies] = await Promise.all([apiCallSites, apiCallStudies])

    let elements = dataSites?.elements ?? []
    if (dataStudies?.elements && Array.isArray(dataStudies.elements) && Array.isArray(elements)) {
      elements = [...dataStudies.elements, ...elements]
    }

    setViewMode()
    return { elements, totalElements: elements.length }
  }

  const exportDocs = () => {
    formRefLocal.current?.validateForm().then((errors) => {
      if (Object.keys(errors).length) {
        context.service.toast.error(t('PLS_APPLY_FILTERS'))
      } else {
        doExport()
      }
    })
  }

  const doExport = async () => {
    try {
      const request = getAssembledData(true)
      await eosApiService.getEosStatusDocuments({
        ...request,
        activityTypes: request.activityTypes?.join(',')
      } as any)
      showModal(<ExportInProcessModal />)
    } catch (error: unknown) {
      const errData = error as { data?: { message?: string } }
      context.service.toast.error(errData?.data?.message ?? 'Error')
    }
  }

  const doFilter = () => {
    setWasFiltered(true)
    setLoading(true)

    getItems({}, true)
      .then((data) => {
        if (!data) return

        const sitesArr = data.elements.filter((ele: FileRow) =>
          'siteFolderName' in ele ? !!ele.siteFolderName?.length : false
        )
        const studyArr = data.elements.filter((ele: FileRow) =>
          'siteFolderName' in ele ? !ele.siteFolderName?.length : true
        )

        sitesArr.sort((prev: FileRow, next: FileRow) => {
          const a = prev as EosWorkflowTableRow
          const b = next as EosWorkflowTableRow
          return (
            (a.country ?? '').localeCompare(b.country ?? '') ||
            (a.siteFolderName ?? '').localeCompare(b.siteFolderName ?? '') ||
            (b.formattedWfId ?? '').localeCompare(a.formattedWfId ?? '')
          )
        })

        studyArr.sort((prev: FileRow, next: FileRow) => {
          const a = prev as EosWorkflowTableRow
          const b = next as EosWorkflowTableRow
          return (
            (a.studyFolderName ?? '').localeCompare(b.studyFolderName ?? '') ||
            (b.formattedWfId ?? '').localeCompare(a.formattedWfId ?? '')
          )
        })

        setFilesList([...studyArr, ...sitesArr])
        setTotalItems(data.elements.length)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  // ---------------------------------------------------------------------------
  // Cascading filter logic
  // ---------------------------------------------------------------------------

  const clearProps = (skipUntilProp: string, propsToChange: FilterFormState) => {
    let skip = true
    orderedPropsCaps.forEach((propName) => {
      if (propName === skipUntilProp) {
        skip = false
      }
      if (!skip) {
        ;(propsToChange as Record<string, unknown>)[propName] = null
      }
    })
    setFilterForm({ ...propsToChange })
  }

  const clearHiddenProps = (filterFormData: FilterFormState = cloneDeep(filterForm)) => {
    if (!showStudy(filterFormData)) filterFormData.STUDY = null
    if (!showCountry(filterFormData)) filterFormData.COUNTRY = null
    if (!showSite(filterFormData)) filterFormData.SITE = null
    setFilterForm(filterFormData)
  }

  const isStudyEnabled = (): boolean => {
    return !!studyDocumentsFilterOptions.STUDY?.length
  }

  const onLevelChange = async (
    auto?: boolean,
    preselect?: boolean,
    filterFormData: FilterFormState = cloneDeep(filterForm)
  ): Promise<void> => {
    preselect = preselect === undefined ? true : preselect

    clearHiddenProps(filterFormData)

    if (filterFormData.level || auto) {
      // Ensure study options are loaded. downloadStudies returns the actual
      // options array so we don't rely on stale React closure state.
      let currentStudyOptions = studyDocumentsFilterOptions.STUDY
      if (!currentStudyOptions) {
        currentStudyOptions = await downloadStudies()
      }

      if (preselect && currentStudyOptions?.length === 1) {
        filterFormData.STUDY = currentStudyOptions[0]
      }

      await onStudyChange(auto, preselect, filterFormData, currentStudyOptions)
    } else {
      clearProps(LEVEL.STUDY, filterFormData)
    }
  }

  const onStudyChange = async (
    auto?: boolean,
    preselect?: boolean,
    filterFormData: FilterFormState = cloneDeep(filterForm),
    studyOptions?: EosSelectOption[]
  ): Promise<void> => {
    preselect = preselect === undefined ? true : preselect

    // Use the passed-in studyOptions (fresh from downloadStudies) to avoid
    // stale closure reads of studyDocumentsFilterOptions.STUDY.
    const availableStudies = studyOptions ?? studyDocumentsFilterOptions.STUDY
    if (filterFormData.STUDY?.value && !availableStudies?.find((s) => s.value === filterFormData.STUDY?.value)) {
      filterFormData.STUDY = null
    }

    if (filterFormData.STUDY) {
      if (showCountry(filterFormData)) {
        const results = (await eosApiService.getCountriesUnderStudy(filterFormData.STUDY.value as number)) as string[]
        const countryOptions = convertServerValues(results, 'country')
        countryOptions.unshift({ label: t('ETMF_LABEL_SELECT_ALL_COUNTRIES'), value: null })

        if (preselect && countryOptions.length === 1) {
          filterFormData.COUNTRY = countryOptions[0]
          setFilterForm(filterFormData)
        }

        setStudyDocumentsFilterOptions((prev) => ({ ...prev, COUNTRY: countryOptions }))
        await onCountryChange(auto, preselect, filterFormData)
      }
    } else {
      clearProps(LEVEL.STUDY, filterFormData)
    }
  }

  const onCountryChange = async (
    auto?: boolean,
    preselect?: boolean,
    filterFormData: FilterFormState = cloneDeep(filterForm)
  ): Promise<void> => {
    preselect = preselect === undefined ? true : preselect

    if (filterFormData.COUNTRY) {
      if (showSite(filterFormData)) {
        const results = (await eosApiService.getSitesUnderStudy(filterFormData.STUDY!.value as number, [
          filterFormData.COUNTRY.value as string
        ])) as EosSiteInfo[]
        const siteOptions = convertServerValues(results, 'site')
        siteOptions.unshift({ label: t('ETMF_LABEL_SELECT_ALL_SITES'), value: null })

        if (preselect && siteOptions.length === 1) {
          filterFormData.SITE = siteOptions[0]
          setFilterForm(filterFormData)
        }

        setStudyDocumentsFilterOptions((prev) => ({ ...prev, SITE: siteOptions }))
      }
    } else {
      clearProps(LEVEL.COUNTRY, filterFormData)
    }
  }

  // ---------------------------------------------------------------------------
  // Actions Menu
  // ---------------------------------------------------------------------------

  const menuStudyDocumentsOptions = {
    menuId: 'page',
    items: [
      {
        value: t('BTN_FILTER_EXPORT'),
        action: () => exportDocs()
      }
    ]
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div id="eos-study-docs" className="eos-docs-table-container">
      <div className="sub-page-header mt21">
        <div className="d-flex flex-wrap">
          <h2 className="align-left align-top">{t('EOS_STATUS_DOCUMENTS')}</h2>
          <div className="align-right align-bottom d-flex">
            <ActionsMenuBig menuId={menuStudyDocumentsOptions.menuId} items={menuStudyDocumentsOptions.items} />
            <FilterTrigger noPersist onToggle={(collapsed) => setIsFilterCollapsed(collapsed)} />
          </div>
        </div>
      </div>

      <Collapse in={isFilterCollapsed}>
        <div className="filter-wrapper">
          <Formik<FormValues>
            validateOnChange
            validateOnMount={false}
            innerRef={formRefLocal}
            validate={(values) => {
              return validationSchema
                .validate(values, { abortEarly: false, context: values })
                .then(() => ({}))
                .catch((err: any) => yupToFormErrors(err))
            }}
            enableReinitialize={false}
            initialValues={{ filter: initialValues }}
            onReset={() => {
              setInitialValues({ data: cloneDeep(defaultFormData) })
            }}
            onSubmit={() => {
              doFilter()
            }}
          >
            {({ values, setFieldValue, handleSubmit, handleReset }) => (
              <div>
                <div className="row">
                  {/* === Column 1: Level radio === */}
                  <div className="col-xs-4">
                    <FormGroup controlId="eos-filter-level">
                      <ControlLabel className="mb0 fs14">{t('EMTF_LEVEL')}</ControlLabel>
                      <Form.Check
                        type="radio"
                        name="filter.data.level"
                        className="classic w100p"
                        value={LEVEL.STUDY}
                        checked={values.filter.data.level === LEVEL.STUDY}
                        onChange={() => {
                          const formData = cloneDeep(filterForm)
                          setFieldValue('filter.data.level', LEVEL.STUDY)
                          formData.level = LEVEL.STUDY
                          setFilterForm(formData)
                          onLevelChange(undefined, undefined, formData)
                        }}
                        id={`eos-level-${LEVEL.STUDY}`}
                        label={t('EOS_SETUP_STUDY')}
                      />
                      <Form.Check
                        type="radio"
                        name="filter.data.level"
                        className="classic w100p fs14"
                        value={LEVEL.COUNTRY}
                        checked={values.filter.data.level === LEVEL.COUNTRY}
                        onChange={() => {
                          const formData = cloneDeep(filterForm)
                          setFieldValue('filter.data.level', LEVEL.COUNTRY)
                          formData.level = LEVEL.COUNTRY
                          setFilterForm(formData)
                          onLevelChange(undefined, undefined, formData)
                        }}
                        id={`eos-level-${LEVEL.COUNTRY}`}
                        label={t('EMTF_MD_COUNTRY')}
                      />
                      <Form.Check
                        type="radio"
                        name="filter.data.level"
                        className="classic w100p fs14"
                        value={LEVEL.SITE}
                        checked={values.filter.data.level === LEVEL.SITE}
                        onChange={() => {
                          const formData = cloneDeep(filterForm)
                          setFieldValue('filter.data.level', LEVEL.SITE)
                          formData.level = LEVEL.SITE
                          setFilterForm(formData)
                          onLevelChange(undefined, undefined, formData)
                        }}
                        id={`eos-level-${LEVEL.SITE}`}
                        label={t('EMTF_MD_SITE')}
                      />
                    </FormGroup>
                  </div>

                  {/* === Column 2: Study / Country / Site selects === */}
                  <div className="col-xs-4">
                    <FormGroup controlId="eos-filter-study">
                      <ControlLabel className="fs14 with-asterisk">{t('REPORTS_FILTER_LABEL_STUDY')}</ControlLabel>
                      <Select
                        className="fs14"
                        placeholder={<div className="placeholder-text">{t('SELECT_STUDY')}</div>}
                        name="filter.data.study"
                        value={values.filter.data.study}
                        options={studyDocumentsFilterOptions.STUDY}
                        onChange={(option: any) => {
                          const filterFormData = cloneDeep(filterForm)
                          if (option === null) {
                            setTimeout(() => setFieldValue('filter.data.study', 0), 0)
                            filterFormData.STUDY = null
                          } else {
                            setTimeout(() => setFieldValue('filter.data.study', option.value), 0)
                            filterFormData.STUDY = option
                          }
                          setTimeout(() => {
                            setFieldValue('filter.data.countries', 0)
                            setFieldValue('filter.data.sites', 0)
                          }, 0)
                          filterFormData.COUNTRY = null
                          filterFormData.SITE = null
                          setFilterForm(filterFormData)
                          onStudyChange(undefined, undefined, filterFormData)
                        }}
                        isClearable
                        isDisabled={false}
                      />
                      <ErrorMessageStyled name="filter.data.study" />
                    </FormGroup>

                    {showCountry() && (
                      <FormGroup controlId="eos-filter-country">
                        <ControlLabel className="fs14 with-asterisk">{t('EMTF_MD_COUNTRIES')}</ControlLabel>
                        <Select
                          className="fs14"
                          placeholder={<div className="placeholder-text">{t('ETMF_LABEL_SELECT_COUNTRY')}</div>}
                          name="filter.data.countries"
                          value={values.filter.data.countries}
                          options={studyDocumentsFilterOptions.COUNTRY}
                          onChange={(option: any) => {
                            const filterFormData = cloneDeep(filterForm)
                            if (option === null) {
                              setTimeout(() => setFieldValue('filter.data.countries', 0), 0)
                              filterFormData.COUNTRY = null
                            } else {
                              setTimeout(() => setFieldValue('filter.data.countries', option.value), 0)
                              filterFormData.COUNTRY = option
                            }
                            setTimeout(() => setFieldValue('filter.data.sites', 0), 0)
                            filterFormData.SITE = null
                            setFilterForm(filterFormData)
                            onCountryChange(undefined, undefined, filterFormData)
                          }}
                          isClearable
                          isDisabled={!values.filter.data.study}
                        />
                        <ErrorMessageStyled name="filter.data.countries" />
                      </FormGroup>
                    )}

                    {showSite() && (
                      <FormGroup controlId="eos-filter-site">
                        <ControlLabel className="fs14 with-asterisk">{t('EMTF_MD_SITES')}</ControlLabel>
                        <Select
                          className="fs14"
                          placeholder={<div className="placeholder-text">{t('SELECT_SITE')}</div>}
                          name="filter.data.sites"
                          value={values.filter.data.sites}
                          options={studyDocumentsFilterOptions.SITE}
                          onChange={(option: any) => {
                            const filterFormData = cloneDeep(filterForm)
                            if (option === null) {
                              setTimeout(() => setFieldValue('filter.data.sites', 0), 0)
                              filterFormData.SITE = null
                            } else {
                              setTimeout(() => setFieldValue('filter.data.sites', option.value), 0)
                              filterFormData.SITE = option
                            }
                            setFilterForm(filterFormData)
                          }}
                          isClearable
                          isDisabled={values.filter.data.study === 0 || values.filter.data.countries === 0}
                        />
                        <ErrorMessageStyled name="filter.data.sites" />
                      </FormGroup>
                    )}
                  </div>

                  {/* === Column 3: Activity / View mode === */}
                  <div className="col-xs-4">
                    {isLegacy && (
                      <div>
                        <FormGroup controlId="eos-filter-activity-legacy">
                          <ControlLabel className="mb0">{t('ACTIVITY_TITLE')}</ControlLabel>
                          {VIEW_MODES_LEGACY.map((item, index) => (
                            <Form.Check
                              key={index}
                              type="radio"
                              name="filter.data.activity"
                              className="classic w100p fs14"
                              value={item.value as string}
                              checked={values.filter.data.activity === item.value}
                              onChange={() => {
                                setFilterForm((prev) => ({
                                  ...prev,
                                  activity: item.value as string
                                }))
                                setFieldValue('filter.data.activity', item.value)
                              }}
                              id={`eos-activity-legacy-${item.value}`}
                              label={item.label}
                            />
                          ))}
                        </FormGroup>

                        {values.filter.data.activity === EOS_ACTIVITY.ACKNOWLEDGEMENT && (
                          <FormGroup controlId="eos-filter-ackn-status">
                            <ControlLabel className="mb0">{t('ACKNOWLEDGEMENT_STATUS')}</ControlLabel>
                            {ACKNOWLEDGEMENT_STATUSES.map((item, index) => (
                              <Form.Check
                                key={index}
                                type="radio"
                                name="filter.data.ackn_status"
                                className="classic w100p fs14"
                                value={item.value as string}
                                checked={values.filter.data.ackn_status === item.value}
                                onChange={() => {
                                  setFilterForm((prev) => ({ ...prev, status: item.value as string }))
                                  setFieldValue('filter.data.ackn_status', item.value)
                                  setFieldValue('filter.data.status', item.value)
                                }}
                                id={`eos-ackn-status-${item.value}`}
                                label={item.label}
                              />
                            ))}
                          </FormGroup>
                        )}

                        {values.filter.data.activity === EOS_ACTIVITY.DOWNLOAD && (
                          <FormGroup controlId="eos-filter-download-status">
                            <ControlLabel className="mb0">{t('DOWNLOAD_STATUS')}</ControlLabel>
                            {DOWNLOAD_STATUSES.map((item, index) => (
                              <Form.Check
                                key={index}
                                type="radio"
                                name="filter.data.download_status"
                                className="classic w100p fs14"
                                value={item.value as string}
                                checked={values.filter.data.download_status === item.value}
                                onChange={() => {
                                  setFilterForm((prev) => ({ ...prev, status: item.value as string }))
                                  setFieldValue('filter.data.download_status', item.value)
                                  setFieldValue('filter.data.status', item.value)
                                }}
                                id={`eos-dl-status-${item.value}`}
                                label={item.label}
                              />
                            ))}
                          </FormGroup>
                        )}
                      </div>
                    )}

                    {!isLegacy && (
                      <div>
                        <FormGroup controlId="eos-filter-view-mode">
                          <ControlLabel>{t('VIEW')}</ControlLabel>
                          {VIEW_MODES.map((item, index) => (
                            <Form.Check
                              key={index}
                              type="radio"
                              name="filter.data.viewMode"
                              className="classic w100p fs14"
                              value={item.value as string}
                              checked={values.filter.data.viewMode === item.value}
                              onChange={() => {
                                setFilterForm((prev) => ({
                                  ...prev,
                                  viewMode: item.value as string
                                }))
                                setTimeout(() => setFieldValue('filter.data.viewMode', item.value), 0)
                              }}
                              id={`eos-view-mode-${item.value}`}
                              label={item.label}
                            />
                          ))}
                        </FormGroup>

                        {values.filter.data.viewMode === EOS_ACTIVITY.WORKFLOW && (
                          <FormGroup controlId="eos-filter-wf-status">
                            <ControlLabel className="fs14">{t('EOS_DASHBOARD_WORKFLOW_STATUS_LABEL')}</ControlLabel>
                            <Select
                              className="fs14"
                              placeholder={
                                <div className="placeholder-text">
                                  {t('EOS_DASHBOARD_WORKFLOW_STATUS_SELECT_PLACEHOLDER')}
                                </div>
                              }
                              name="filter.data.activity"
                              value={values.filter.data.activity}
                              options={WF_STATUS_OPTIONS}
                              onChange={(option: any) => {
                                if (option) {
                                  setFilterForm((prev) => ({
                                    ...prev,
                                    activity: option.value as string
                                  }))
                                  setFieldValue('filter.data.activity', option.value)
                                } else {
                                  setFilterForm((prev) => ({ ...prev, activity: undefined }))
                                  setFieldValue('filter.data.activity', '')
                                }
                              }}
                              isClearable
                              isDisabled={values.filter.data.activity === null}
                            />
                            <ErrorMessageStyled name="filter.data.activity" />
                          </FormGroup>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* === Filter / Clear buttons === */}
                <div>
                  <Button
                    data-action="export"
                    className="btn btn-primary"
                    onClick={() => handleSubmit()}
                    id="eos-filter-apply-btn"
                  >
                    {t('BTN_FILTER')}
                  </Button>
                  <Button
                    data-action="reset"
                    variant="link"
                    onClick={() => {
                      handleReset()
                      setFilterForm({ level: LEVEL.STUDY })
                    }}
                    id="eos-filter-clear-btn"
                  >
                    {t('BTN_CLEAR_FILTER')}
                  </Button>
                </div>
              </div>
            )}
          </Formik>
        </div>
      </Collapse>

      {/* === Table area === */}
      <div className="table-responsive" style={{ marginTop: '6px' }}>
        {loading ? (
          <Loader blocking />
        ) : (
          <>
            {tableViewMode === EOS_ACTIVITY.USER_ACCESS && (
              <EosOverviewAccessTable
                filesList={filesList as EosAccessTableRow[]}
                wasFiltered={wasFiltered}
                isLegacy={isLegacy}
              />
            )}
            {tableViewMode === EOS_ACTIVITY.WORKFLOW && (
              <EosOverviewWorkflowsTable filesList={filesList as EosWorkflowTableRow[]} wasFiltered={wasFiltered} />
            )}
            {isLegacy && tableViewMode !== EOS_ACTIVITY.USER_ACCESS && (
              <EosOverviewLegacyTable
                filesList={filesList as EosLegacyTableRow[]}
                wasFiltered={wasFiltered}
                tableViewMode={tableViewMode}
              />
            )}
          </>
        )}
      </div>

      {/* === Footer === */}
      {!!(wasFiltered && totalItems) && (
        <div className="bctable-footer">
          <div className="clearfix">
            <div className="total-results fs12">
              <b>{totalItems}</b> <span>{t('PAGINATION_TOTAL_RESULTS')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EosStatusDocuments
